import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "calendar-wrap" }
const _hoisted_2 = { class: "stats-row" }
const _hoisted_3 = { class: "stat" }
const _hoisted_4 = { class: "stat-value" }
const _hoisted_5 = { class: "stat" }
const _hoisted_6 = { class: "stat-value" }
const _hoisted_7 = { class: "stat" }
const _hoisted_8 = { class: "stat-value" }
const _hoisted_9 = {
  key: 0,
  class: "stat"
}
const _hoisted_10 = { class: "stat-value severe-care" }
const _hoisted_11 = { class: "stat" }
const _hoisted_12 = { class: "stat-value" }
const _hoisted_13 = { class: "calendar" }
const _hoisted_14 = { class: "cal-header" }
const _hoisted_15 = { class: "cal-body" }
const _hoisted_16 = {
  key: 0,
  class: "hol-name"
}
const _hoisted_17 = {
  key: 0,
  class: "day-services"
}
const _hoisted_18 = { class: "svc-time" }
const _hoisted_19 = { class: "svc-name" }
const _hoisted_20 = {
  key: 0,
  class: "svc-more"
}
const _hoisted_21 = { class: "patient-stats" }
const _hoisted_22 = { class: "ps-list" }
const _hoisted_23 = { class: "ps-name" }
const _hoisted_24 = { class: "ps-count" }
const _hoisted_25 = { class: "ps-hours" }
const _hoisted_26 = {
  key: 0,
  class: "ps-severe"
}

import { computed } from 'vue';

interface Service {
  date: string;
  patientName: string;
  patientCode: string;
  sugaName: string;
  startTime: string;
  endTime: string;
  sugaMoney: number;
  isFamily: boolean;
  isSevereCare?: boolean;
}

interface Props {
  yearMonth: string;
  services: Service[];
  holidays: Record<string, string>;
}

interface CalendarDay {
  day: number;
  date: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  dayOfWeek: number;
  isFirstOfMonth: boolean;
  monthNum: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WageEmployeeCalendar',
  props: {
    yearMonth: {},
    services: {},
    holidays: {}
  },
  setup(__props: any) {

const props = __props;

const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

const year = computed(() => parseInt(props.yearMonth?.slice(0, 4) || new Date().getFullYear().toString()));
const month = computed(() => parseInt(props.yearMonth?.slice(4, 6) || (new Date().getMonth() + 1).toString()));

const calendarDays = computed<CalendarDay[]>(() => {
  const y = year.value;
  const m = month.value;
  
  const firstDay = new Date(y, m - 1, 1);
  const startDay = new Date(firstDay);
  startDay.setDate(startDay.getDate() - firstDay.getDay());
  
  const days: CalendarDay[] = [];
  const current = new Date(startDay);
  const today = new Date();
  const todayStr = formatDateStr(today);
  
  for (let i = 0; i < 42; i++) {
    const isCurrentMonth = current.getMonth() === m - 1;
    const dateStr = formatDateStr(current);
    const isFirstOfMonth = current.getDate() === 1;
    
    days.push({
      day: current.getDate(),
      date: dateStr,
      isCurrentMonth,
      isToday: dateStr === todayStr,
      dayOfWeek: current.getDay(),
      isFirstOfMonth,
      monthNum: current.getMonth() + 1
    });
    
    current.setDate(current.getDate() + 1);
  }
  
  return days;
});

const formatDayNumber = (day: CalendarDay): string => {
  if (day.isFirstOfMonth) return `${day.monthNum}/${day.day}`;
  return String(day.day);
};

const formatDateStr = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatTime = (time?: string): string => {
  if (!time) return '';
  const str = time.toString().replace(/[^0-9]/g, '');
  if (str.length >= 4) return `${str.slice(0, 2)}:${str.slice(2, 4)}`;
  return time;
};

const getDayServices = (dateStr: string): Service[] => {
  return props.services.filter(s => {
    const serviceDate = s.date?.includes('-') ? s.date : formatServiceDate(s.date);
    return serviceDate === dateStr;
  }).sort((a, b) => {
    const timeA = a.startTime?.replace(/[^0-9]/g, '') || '';
    const timeB = b.startTime?.replace(/[^0-9]/g, '') || '';
    return timeA.localeCompare(timeB);
  });
};

const formatServiceDate = (date: string): string => {
  if (!date) return '';
  const str = date.toString().replace(/[^0-9]/g, '');
  if (str.length === 8) return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
  return date;
};

const isHoliday = (day: CalendarDay): boolean => {
  if (day.dayOfWeek === 0) return true;
  return !!props.holidays[day.date];
};

const getHolidayName = (day: CalendarDay): string | null => {
  if (!day.isCurrentMonth) return null;
  return props.holidays[day.date] || null;
};

const normalServiceCount = computed(() => props.services.filter(s => !s.isSevereCare).length);
const severeCareCount = computed(() => props.services.filter(s => s.isSevereCare).length);

const workDays = computed(() => {
  const uniqueDates = new Set(props.services.filter(s => !s.isSevereCare).map(s => s.date?.includes('-') ? s.date : formatServiceDate(s.date)));
  return uniqueDates.size;
});

const totalHoursText = computed(() => {
  let totalMinutes = 0;
  props.services.filter(s => !s.isSevereCare).forEach(s => {
    const start = formatTime(s.startTime).split(':');
    const end = formatTime(s.endTime).split(':');
    if (start.length >= 2 && end.length >= 2) {
      const startMins = parseInt(start[0]) * 60 + parseInt(start[1]);
      const endMins = parseInt(end[0]) * 60 + parseInt(end[1]);
      totalMinutes += Math.max(0, endMins - startMins);
    }
  });
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`;
});

const uniquePatients = computed(() => {
  const patients = new Set(props.services.filter(s => !s.isSevereCare).map(s => s.patientName));
  return Array.from(patients).filter(Boolean);
});

const patientStats = computed(() => {
  const stats: Record<string, { count: number; minutes: number; severeCareCount: number }> = {};
  props.services.forEach(s => {
    if (!stats[s.patientName]) stats[s.patientName] = { count: 0, minutes: 0, severeCareCount: 0 };
    if (s.isSevereCare) {
      stats[s.patientName].severeCareCount++;
    } else {
      stats[s.patientName].count++;
      const start = formatTime(s.startTime).split(':');
      const end = formatTime(s.endTime).split(':');
      if (start.length >= 2 && end.length >= 2) {
        const startMins = parseInt(start[0]) * 60 + parseInt(start[1]);
        const endMins = parseInt(end[0]) * 60 + parseInt(end[1]);
        stats[s.patientName].minutes += Math.max(0, endMins - startMins);
      }
    }
  });
  return Object.entries(stats)
    .map(([name, data]) => {
      const hours = Math.floor(data.minutes / 60);
      const mins = data.minutes % 60;
      return { 
        name, 
        count: data.count, 
        hours: mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`,
        severeCareCount: data.severeCareCount
      };
    })
    .sort((a, b) => b.count - a.count);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[0] || (_cache[0] = _createElementVNode("span", { class: "stat-label" }, "근무일수", -1)),
        _createElementVNode("span", _hoisted_4, _toDisplayString(workDays.value) + "일", 1)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _cache[1] || (_cache[1] = _createElementVNode("span", { class: "stat-label" }, "총 근무시간", -1)),
        _createElementVNode("span", _hoisted_6, _toDisplayString(totalHoursText.value), 1)
      ]),
      _createElementVNode("div", _hoisted_7, [
        _cache[2] || (_cache[2] = _createElementVNode("span", { class: "stat-label" }, "서비스 횟수", -1)),
        _createElementVNode("span", _hoisted_8, _toDisplayString(normalServiceCount.value) + "회", 1)
      ]),
      (severeCareCount.value > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _cache[3] || (_cache[3] = _createElementVNode("span", { class: "stat-label" }, "중증수급자 가산", -1)),
            _createElementVNode("span", _hoisted_10, _toDisplayString(severeCareCount.value) + "회", 1)
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_11, [
        _cache[4] || (_cache[4] = _createElementVNode("span", { class: "stat-label" }, "담당 수급자", -1)),
        _createElementVNode("span", _hoisted_12, _toDisplayString(uniquePatients.value.length) + "명", 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_13, [
      _createElementVNode("div", _hoisted_14, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(weekDays, (day) => {
          return _createElementVNode("div", {
            class: _normalizeClass(["weekday", { sun: day === '일', sat: day === '토' }]),
            key: day
          }, _toDisplayString(day), 3)
        }), 64))
      ]),
      _createElementVNode("div", _hoisted_15, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calendarDays.value, (day, idx) => {
          return (_openBlock(), _createElementBlock("div", {
            key: idx,
            class: _normalizeClass(["cal-day", { 
            'other': !day.isCurrentMonth,
            'today': day.isToday,
            'worked': getDayServices(day.date).length > 0,
            'holiday': isHoliday(day)
          }])
          }, [
            _createElementVNode("div", {
              class: _normalizeClass(["day-num", { sun: day.dayOfWeek === 0, sat: day.dayOfWeek === 6, hol: isHoliday(day) && day.dayOfWeek !== 0 }])
            }, [
              _createTextVNode(_toDisplayString(formatDayNumber(day)) + " ", 1),
              (getHolidayName(day))
                ? (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(getHolidayName(day)), 1))
                : _createCommentVNode("", true)
            ], 2),
            (day.isCurrentMonth)
              ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getDayServices(day.date).slice(0, 3), (service, sIdx) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: sIdx,
                      class: _normalizeClass(["svc-item", { 'family': service.isFamily, 'severe-care': service.isSevereCare }])
                    }, [
                      _createElementVNode("span", _hoisted_18, _toDisplayString(service.isSevereCare ? '중증' : formatTime(service.startTime)), 1),
                      _createElementVNode("span", _hoisted_19, _toDisplayString(service.patientName), 1)
                    ], 2))
                  }), 128)),
                  (getDayServices(day.date).length > 3)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_20, "+" + _toDisplayString(getDayServices(day.date).length - 3), 1))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
          ], 2))
        }), 128))
      ])
    ]),
    _createElementVNode("div", _hoisted_21, [
      _cache[5] || (_cache[5] = _createElementVNode("div", { class: "ps-title" }, "수급자별 서비스", -1)),
      _createElementVNode("div", _hoisted_22, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(patientStats.value, (p) => {
          return (_openBlock(), _createElementBlock("div", {
            key: p.name,
            class: "ps-item"
          }, [
            _createElementVNode("span", _hoisted_23, _toDisplayString(p.name), 1),
            _createElementVNode("span", _hoisted_24, _toDisplayString(p.count) + "회", 1),
            _createElementVNode("span", _hoisted_25, _toDisplayString(p.hours), 1),
            (p.severeCareCount > 0)
              ? (_openBlock(), _createElementBlock("span", _hoisted_26, "중증" + _toDisplayString(p.severeCareCount), 1))
              : _createCommentVNode("", true)
          ]))
        }), 128))
      ])
    ])
  ]))
}
}

})